import React, { useEffect, useMemo, useState } from 'react';
import { CatalogFilter, CatalogParams } from './Catalog';
import { SidebarContainer } from '../components';
import { APIResponse } from '@arema/components/Classes';
import { ReportResponse } from '../AdminClasses';
import { addCommas, bindChange, fromFormatLocal, fromUnixLocal, getIndexes, getNowLocal } from '@arema/components/Util';
import { Button, Field, Groupper, Header, Icon, Input, Message, Pagination, Table, Checkbox } from 'react-frontier2';
import { Link, useSearchParams } from 'react-router-dom';
import { useUser } from '../AdminHooks';
import { DateTime } from 'luxon';
import Validator from '@arema/components/Validator';
import API from '../API';
import classNames from 'classnames';
import UserAccess from '../UserAccess';
import EventReport from './reports/EventReport';
import PdvSalesReport from './reports/PdvSalesReport';
import IncomeReport from './reports/IncomeReport';
import BuyersReport from './reports/BuyersReport';
import '../style/reports.scss';

interface ReportCatalogOptions{
	title: string,
	filters: CatalogFilter[][],
	getData: (params: CatalogParams, offset?: number, count?: number)=>Promise<APIResponse<ReportResponse<any>>>
	pageSize?: number,
	tableWidth?: number,
	render?: (props: ReportResponse<any>)=>JSX.Element,
	table?: {
		name: string,
		key?: string,
		collapsing?: boolean,
		centered?: boolean,
		href?: (val: any, obj: any)=>string,
		render?: (val: any, obj: any)=>any
	}[],
	rowActions?: (data: any)=>{
		text: string,
		onClick?: any,
		url?: string,
	}[]
}

var formatTime = (date_start: number, date_end: number)=>{
	var time = (date_end-date_start)/60
	var form = [];
	if(time>=60){
		form.push(`${Math.floor(time/60)}h`);
	}
	form.push(`${Math.floor(time%60)}m`);
	return form.join(', ');
}

const DATE_START_END_FILTERS : CatalogFilter[] = [
	{
		key: 'date_start', 
		type: 'calendar', 
		label: 'Fecha Inicio', 
		shorthand: 's', 
		defaultValue: getNowLocal().plus({ day: -7 }).toJSDate(),
		rules: [
			{ rule: 'empty', prompt: 'La fecha inicio no es válida.' },
		]
	}, 
	{
		key: 'date_end',
		type: 'calendar',
		label: 'Fecha Fin',
		shorthand: 'e',
		defaultValue: getNowLocal().toJSDate(),
		rules: [
			{ rule: 'empty', prompt: 'La fecha fin no es válida.' },
		]
	}
]

const REPORTS : { [x: string]: ReportCatalogOptions } = {
	INVOICES: {
		title: 'Facturas',
		pageSize: 60,
		filters: [DATE_START_END_FILTERS],
		getData: (params: CatalogParams, offset: number, count: number)=>{
			var ds = DateTime.fromJSDate(params.date_start);
			if(!ds.isValid) ds = getNowLocal().plus({ days: -7 });

			var de = DateTime.fromJSDate(params.date_end);
			if(!de.isValid) de = getNowLocal()

			return API.getInvoices(ds.toSeconds(), de.toSeconds(), offset, count)
		},
		table: [
			{ name: 'ID', key: 'invoice_id', collapsing: true },
			{ name: 'Orden', key: 'order_hash', collapsing: true },
			{ name: 'Nombre', key: 'legal_name' },
			{ name: 'Email', key: 'email' },
			{ name: 'RFC', key: 'rfc', collapsing: true },
			{ name: 'Subtotal', key: 'amount', collapsing: true, centered: true, render: (v, o)=>addCommas(v/1.16) },
			{ name: 'IVA', key: 'amount', collapsing: true, centered: true, render: (v, o)=>addCommas((v/1.16)*0.16) },
			{ name: 'Total', key: 'amount', collapsing: true, centered: true, render: addCommas },
		],
		rowActions: (v: any)=>([
			{ text: 'Ver orden', url: `/orders/${v.order_hash}` },
			{ text: 'Descargar PDF', url: v.download_pdf },
			{ text: 'Descargar ZIP', url: v.download_zip },
		])
	},
	INCOME: {
		title: 'Ingresos',
		filters: [DATE_START_END_FILTERS],
		tableWidth: 10000,
		getData: (params: CatalogParams)=>{
			var ds = DateTime.fromJSDate(params.date_start);
			if(!ds.isValid) ds = getNowLocal().plus({ days: -7 });

			var de = DateTime.fromJSDate(params.date_end);
			if(!de.isValid) de = getNowLocal()

			return API.getIncomeReport(ds.toSeconds(), de.toSeconds())
		},
		render: (IncomeReport as any),
	},
	PDV_CORTES: {
		title: 'PDV Cortes',
		filters: [[{
			key: 'date',
			label: 'Fecha',
			type: 'calendar',
			defaultValue: getNowLocal().toJSDate(),
			rules: [{ rule: 'empty' }]
		}]],
		tableWidth: 1000,
		getData: (params: CatalogParams)=>{
			return API.getPdvCortesReport(params.date.getTime()/1000);
		},
		table: [
			{ name: 'ID', key: 'pdv_id', collapsing: true },
			{ name: 'PDV', key: 'pdv_name', collapsing: false },
			{ name: 'Inicio', key: 'date_start', collapsing: true, render: v=>fromUnixLocal(v).toFormat('dd/MM/yy HH:mm') },
			{ name: 'Fin', key: 'date_end', collapsing: true, render: v=>{
				if(!v) return <Icon name='minus' color='gray' />
				return fromUnixLocal(v).toFormat('dd/MM/yy HH:mm')
			}},
			{ name: 'Tiempo', key: 'date_start', collapsing: true, render: (v, o)=>{
				if(!o.date_end) return <Icon name='minus' color='gray' />
				return formatTime(o.date_start, o.date_end)
			}},
			{ name: 'Usuario', key: 'users', render: (v, o)=>{
				return v ? v.map((a: any)=>(
					<Link className='normal' to={`/users/${a.user_id}`} key={`USR-${o.pdv_id}-${a.user_id}`}>[{a.user_id}] {a.first_name} {a.last_name}</Link>
				)) : null
			}}
		]
	},
	PDV_SALES: {
		title: 'PDV Ventas',
		filters: [DATE_START_END_FILTERS],
		tableWidth: 1000,
		getData: (params: CatalogParams)=>{
			var ds = DateTime.fromJSDate(params.date_start);
			if(!ds.isValid) ds = getNowLocal().plus({ days: -7 });

			var de = DateTime.fromJSDate(params.date_end);
			if(!de.isValid) de = getNowLocal()
			return API.getPdvSalesReport(ds.toSeconds(), de.toSeconds());
		},
		render: PdvSalesReport,
	},
	EVENT_PAYOUTS: {
		title: 'Liquidaciones',
		filters: [DATE_START_END_FILTERS],
		getData: (params: CatalogParams)=>{
			var ds = DateTime.fromJSDate(params.date_start);
			if(!ds.isValid) ds = getNowLocal().plus({ days: -7 });

			var de = DateTime.fromJSDate(params.date_end);
			if(!de.isValid) de = getNowLocal()

			return API.getPayoutsReport(ds.toSeconds(), de.toSeconds());
		},
		table: [
			{ name: 'ID', key: 'event_id', collapsing: true },
			{ name: 'Evento', key: 'event_name' },
			{ name: 'Liquidación', key: 'liquidacion', collapsing: true, render: v=>addCommas(v) },
			{ name: 'Anticipo', key: 'anticipo', collapsing: true, render: v=>addCommas(v) },
			{ name: 'Retorno', key: 'retorno', collapsing: true, render: v=>addCommas(v) },
			{ name: 'Efectivo', key: 'efectivo_taquilla', collapsing: true, render: v=>addCommas(v) },
			{ name: 'Otros', key: 'otros', collapsing: true, render: v=>addCommas(v) },
		]
	},
	DATES_BALANCE: {
		title: "Estados de cuenta",
		filters: [DATE_START_END_FILTERS, [
			{ type: 'message', label: 'La fecha seleccionada esa la fecha del calendario', key: 'ADV' },
		]],
		tableWidth: 1200,
		getData: (params: CatalogParams)=>{
			var ds = DateTime.fromJSDate(params.date_start);
			if(!ds.isValid) ds = getNowLocal().plus({ days: -7 });

			var de = DateTime.fromJSDate(params.date_end);
			if(!de.isValid) de = getNowLocal()

			return API.getDateBalanceReport(ds.toSeconds(), de.toSeconds());
		},
		table: [
			{ name: 'ID', key: 'event_id', collapsing: true },
			{ name: 'IDCal', key: 'date_id', collapsing: true },
			{ name: 'Evento', key: 'event_name' },
			{ name: 'Boletos', key: 'tickets', render: v=>addCommas(v, false), collapsing: true },
			{ name: 'Ingresos', key: 'ticket_cost', render: v=>addCommas(v), collapsing: true },
			{ name: 'CxS', key: 'commission', render: v=>addCommas(v), collapsing: true },
			{ name: 'Liquidación', key: 'liquidacion', collapsing: true, render: v=>addCommas(v) },
			{ name: 'Anticipo', key: 'anticipo', collapsing: true, render: v=>addCommas(v) },
			{ name: 'Retorno', key: 'retorno', collapsing: true, render: v=>addCommas(v) },
			{ name: 'Efectivo', key: 'efectivo_taquilla', collapsing: true, render: v=>addCommas(v) },
			{ name: 'Otros', key: 'otros', collapsing: true, render: v=>addCommas(v) },
			{ name: 'Balance', key: 'liquidacion', collapsing: true, render: (v, o)=>{
				return addCommas(o.ticket_cost-o.total_payout);
			} },
		]
	}
}

interface CatalogFiltersProps{
	className?: string,
	filters: CatalogFilter[][],
	setParams?: boolean,
	onDataChange: (data: any, params: any)=>void,
}

var CatalogFilters = (props: CatalogFiltersProps)=>{
	var [filterData, setFilterData] = useState<any>(null);
	var [params, setParams] = useSearchParams();

	var updateParams = (data: any)=>{
		var params : any = {};
		var flat_filters = props.filters.flat();
		for(var i in data){
			if(data[i]===null || data[i]===undefined) continue;
			var sh = flat_filters.find(a=>a.key===i);
			if(!sh) continue;
			params[sh.shorthand || sh.key] = data[i];
			if(sh.type==='calendar'){
				params[sh.shorthand || sh.key] = DateTime.fromJSDate(params[sh.shorthand || sh.key]).toFormat('yyyy-MM-dd');
			}
		}
		if(props.setParams){
			setParams(params);
		}
		if(props.onDataChange) props.onDataChange(data, params);
		return params;
	}

	useEffect(()=>{
		var filter_data : any = {};

		if(props.filters){
			for(var i of props.filters.flat()){
				var d = params.get(i.shorthand);
				if(d===null && i.defaultValue){
					filter_data[i.key] = i.defaultValue || null;
				}else{
					if(i.type==='calendar'){
						filter_data[i.key] = fromFormatLocal(d, 'yyyy-MM-dd').toJSDate();
					}else if(i.type==='checkbox'){
						filter_data[i.key] = parseInt(d)==1;
					}else{
						filter_data[i.key] = d;
					}
				}
			}

			if(Object.keys(filter_data).length>0){
				setFilterData(filter_data);
				updateParams(filter_data);
			}
		}
	}, [props.filters]);

	var onFilterChange = (key: string)=>{
		return (val: any)=>{
			var new_data = {
				...filterData,
				[key]: val,
			};
			setFilterData(new_data);
			updateParams(new_data);
		}
	}

	return <div className={classNames("filters", props.className)}>
		{props.filters.map((a, i)=>{
			const Elem = a.length==1 ? (props: { amount: number, children: any })=><div style={{ marginBottom: 10 }}>{props.children}</div> : Field;
			return <Elem amount={a.length} key={`filterfield-${i}`}>
				{a.map(b=>(
					b.type==='text' ? (
						<Input key={`filter${i}-${b.key}`} label={b.label} value={filterData ? (filterData[b.key] || '') : ''} onChange={onFilterChange(b.key)} />
					) : b.type==='checkbox' ? (
						<Field key={`filter${i}-${b.key}`}>
							<Checkbox label={b.label} checked={filterData ? (filterData[b.key] || false) : false} onChange={bindChange(onFilterChange(b.key))} />
						</Field>
					) : b.type==='calendar' ? (
						<Input 
							key={`filter${i}-${b.key}`} 
							label={b.label} 
							calendar={{
								date: filterData ? (filterData[b.key] || null) : null,
								mode: 'date',
								onSelected: onFilterChange(b.key)
							}} 
						/>
					) : b.type==='message' ? (
						<Message text={b.label || b.defaultValue} />
					) : null
				))}
			</Elem>
		})}
	</div>
}

var ReportCatalog = (props: ReportCatalogOptions)=>{
	var [data, setData] = useState<ReportResponse<any>>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [filterData, setFilterData] = useState<any>(null);
	var [loading, setLoading] = useState<boolean>(null);
	var [page, setPage] = useState<number>(null);

	var searchData = async (page: number=0)=>{
		if(props.getData){
			var filter_rules : any = {};
			for(var i of props.filters.flat()){
				filter_rules[i.key] = i.rules;
			}
			var { valid, prompts } = Validator(filterData, filter_rules);
			setErrorPrompts(prompts);
			if(!valid) return;

			setLoading(true);
			setLoadError(null);
			setData(null);
			setPage(page);
			props.getData(filterData, (page || 0)*(props.pageSize || 30), (props.pageSize || 30)).then(res=>{
				if(res.error) return setLoadError(res.message);
				setData(res.data);
			}).catch(err=>{
				setLoadError(`Hubo un error inesperado cargando el reporte "${props.title}" (LCL-1)`);
			}).finally(()=>{
				setLoading(false);
			})
		}
	}

	var changePage = (p: number)=>{
		setPage(p);
		searchData(p);
	}

	return <div className="ar report-component">
		<Header text={props.title} />
		<Groupper title='Filtros' style={{ width: 500, margin: 'auto' }} actions={(
			<Button text='Buscar' color='black' onClick={()=>searchData(0)} loading={loading} />
		)}>
			<CatalogFilters filters={props.filters} onDataChange={setFilterData} setParams />
			<Message type='error' list={errorPrompts} />
		</Groupper>
		{loading ? (
			<Header loading style={{ marginTop: 20 }} text='Cargando datos' subtext={`Reporte: ${props.title}`} />
		) : !!loadError ? (
			<Header iconName='face-frown-open' style={{ marginTop: 20 }} text='Error' subtext={loadError} />
		) : null}
		{!data ? null : (
			props.table ? (
				<Table striped selectable style={{ margin: 'auto', marginTop: 15, maxWidth: props.tableWidth || 1000 }}>
					<Table.Head
						title={props.title}
						data={[[...props.table.map(a=>a.name), ...(props.rowActions ? [''] : [])]]}
						centeredIndexes={getIndexes(props.table.map(a=>!!a.centered))}
						collapsingIndexes={getIndexes(props.table.map(a=>!!a.collapsing))}
						titleRight={!!data && !!data.downloads && data.downloads.length>0 && (
							<Button iconName="file-download" size="tiny" text='Descargar' color='blue' menu={(
								data.downloads.map(a=>(
									{ text: a.text, iconName: a.icon, as: Link, to: a.url, target: '_blank' }
								))
							)} />
						)}
					/>
					<Table.Body>
						{data.data.map((a, i)=>{
							return <tr key={`RPTRW-${i}`}>
								{props.table.map((v, j)=>(
									<Table.Cell as={v.href ? Link : null} compact={!!v.href} to={v.href ? v.href(a[v.key], a) : undefined} collapsing={v.collapsing} centered={v.centered} key={`RPT-${i}-${j}-${v.key}`}>
										{v.render ? v.render(a[v.key], a) : a[v.key] || <Icon name='minus' color='gray' />}
									</Table.Cell>
								))}
								{props.rowActions && (
									<Table.Cell collapsing>
										<Button icon iconName="ellipsis-h" size="tiny" menu={[
											props.rowActions(a).map((t, ti)=>(
												{ text: t.text, as: t.url ? Link : 'div', to: t.url, target: '_blank', key: `RWACT-${i}-${ti}` }
												// <Dropdown.Item key={} as={t.url ? Link : 'div'} to={t.url} text={t.text} target='_blank' />
											))
										]} />
									</Table.Cell>
								)}
							</tr>
						})}
					</Table.Body>
					{!!props.pageSize && (
						<Table.Footer>
							<Table.Cell row colSpan={1000} style={{ textAlign: 'right' }}>
								<Pagination onPageChange={changePage} page={page} disabled={loading} hasNext={data && data.data && data.data.length>=(props.pageSize || 30)} />
							</Table.Cell>
						</Table.Footer>
					)}
				</Table>
			) : <props.render data={data.data} downloads={data.downloads} />
		)}
	</div>
}

var ReportsContainer = ()=>{
	var { hasAccess } = useUser();
	var ACCESS = useMemo(()=>{
		return {
			INCOME: hasAccess(UserAccess.REPORTS_ADMIN.INCOME),
			EVENTS: hasAccess(UserAccess.EVENTS.VIEW_EVENTS),
			PDV_SALES: hasAccess(UserAccess.REPORTS_ADMIN.PDV_SALES),
			BUYERS: hasAccess(UserAccess.REPORTS_ADMIN.BUYERS),
			EVENT_BALANCE: hasAccess(UserAccess.REPORTS_ADMIN.EVENT_BALANCE),
		}
	}, []);
	return <div className="ar reports-container">
		<SidebarContainer
			header='Reportes'
			routes={[
				{ title: 'Reporte: Facturación', url: '/reportes/facturacion', render: <ReportCatalog {...REPORTS.INVOICES}  /> },
				{ title: 'Reporte: Aforo', url: '/reportes/aforo', render: <ReportCatalog {...REPORTS.INVOICES}  /> },
				{ title: 'Reporte: Ingresos', url: '/reportes/ingresos', render: <ReportCatalog {...REPORTS.INCOME}  /> },
				{ title: 'Reporte: Eventos', url: '/reportes/events', render: <EventReport /> },
				{ title: 'Reporte: PDV Cortes', url: '/reportes/pdv/cortes', render: <ReportCatalog {...REPORTS.PDV_CORTES} /> },
				{ title: 'Reporte: PDV Ventas', url: '/reportes/pdv/sales', render: <ReportCatalog {...REPORTS.PDV_SALES} /> },
				{ title: 'Reporte: Liquidaciones', url: '/reportes/events/payouts', render: <ReportCatalog {...REPORTS.EVENT_PAYOUTS} /> },
				{ title: 'Reporte: Compradores', url: '/reportes/events/buyers', render: <BuyersReport /> },
				{ title: 'Reporte: Estados de cuenta', url: '/reportes/balance', render: <ReportCatalog {...REPORTS.DATES_BALANCE} /> },
			]}
			items={[
				{ type: 'header', text: 'Eventos' },
				{ text: 'Ingresos', url: '/reportes/ingresos', icon: 'sack-dollar', if: ACCESS.INCOME },
				{ text: 'Eventos', url: '/reportes/events', icon: 'masks-theater', if: ACCESS.EVENTS },
				{ text: 'Liquidaciones', url: '/reportes/events/payouts', icon: 'sack-dollar', if: ACCESS.INCOME },
				{ text: 'Compradores', url: '/reportes/events/buyers', icon: 'map-location-dot', if: ACCESS.BUYERS },
				{ type: 'header', text: 'Contabilidad' },
				{ text: 'Facturación', url: '/reportes/facturacion', icon: 'clipboard' },
				{ text: 'Estados de cuenta', url: '/reportes/balance', icon: 'file-invoice-dollar', if: ACCESS.EVENT_BALANCE },
				{ type: 'header', text: 'Taquillas' },
				{ text: 'PDV Cortes', url: '/reportes/pdv/cortes', icon: 'cash-register' },
				{ text: 'PDV Ventas', url: '/reportes/pdv/sales', icon: 'cash-register', if: ACCESS.PDV_SALES },
			]}
			emptyComponent={(
				<Header iconName='file-clipboard' text={'Reportes'} subtext={'Selecciona un reporte'} style={{ color: 'gray' }} />
			)}
		/>
	</div>
}

export default ReportsContainer;